import {
  type PersonOrOUTypeFromExternalData,
  getPersonOrOUDisplayName,
} from '@UI/personPicker/personHelper';
import type { ContentHref } from '@generalTypes/apiTypes';
import {
  selectContentItem,
  selectBaselineContentItem,
} from '@newStore/documentApi/documentApiSelectors';
import { getDiffForArraysOfStrings } from '@newStore/documentUI/transformProposal/asideDiffText';
import { selectAllExternalData } from '@newStore/externalData/externalDataSelectors';
import type { AllExternalData } from '@newStore/externalData/externalDataTypes';
import { createTypedSelector } from '@newStore/genericHelpers';
import type {
  AsideChangeMessageSelector,
  EditAsideContactsOrCreators,
} from '@nodeTypeConfig/configTypes';
import { get, isEqual } from 'lodash';

const mapPersonOrOusToString = (
  values: string[] | undefined,
  externalData: AllExternalData,
): string[] => {
  return (
    values?.map((item) =>
      externalData[item]
        ? getPersonOrOUDisplayName(externalData[item] as PersonOrOUTypeFromExternalData)
        : '',
    ) || []
  );
};

export const selectChangeMessageForPersonPicker: AsideChangeMessageSelector<EditAsideContactsOrCreators> =
  createTypedSelector(
    [
      (state, href: ContentHref) => selectContentItem(state, href),
      (state, href: ContentHref) => selectBaselineContentItem(state, href),
      (state) => selectAllExternalData(state),
      (state, href: ContentHref, config: EditAsideContactsOrCreators) => config,
    ],
    (content, originalContent, externalData, config): string | null => {
      if (!config.property) {
        console.error('No property found for config', config);
        throw Error(`Can not convert ${config.property} to a string`);
      }

      const newValue = get(content, config.property);
      const oldValue = get(originalContent, config.property);

      if (isEqual(newValue, oldValue)) {
        return null;
      }

      const newValueString = mapPersonOrOusToString(newValue, externalData);
      const oldValueString = mapPersonOrOusToString(oldValue, externalData);

      const diffHtml = getDiffForArraysOfStrings(oldValueString, newValueString);

      return diffHtml;
    },
  );
